import React, { useState, useEffect } from 'react';
import { getUsers, updateUser } from '../api';
import { calcularRestanteTrial, formatearRestanteTrial } from './TrialBanner';

const PROFESSION_CHOICES = [
  { value: '',               label: 'Sin profesión' },
  { value: 'psychologist',   label: 'Psicología' },
  { value: 'psychiatrist',   label: 'Psiquiatría' },
  { value: 'nutritionist',   label: 'Nutrición' },
  { value: 'general_doctor', label: 'Medicina General' },
  { value: 'pediatrician',   label: 'Pediatría' },
  { value: 'dentist',        label: 'Odontología' },
  { value: 'physiotherapist',label: 'Kinesiología' },
  { value: 'other',          label: 'Otra' },
];

function ModalAdminUsuarios({ onCerrar }) {
  const [usuarios, setUsuarios]     = useState([]);
  const [cargando, setCargando]     = useState(true);
  const [error, setError]           = useState('');
  const [selecciones, setSelecciones] = useState({}); // { userId: professionValue }

  const meUser = JSON.parse(localStorage.getItem('user') || '{}');

  useEffect(() => {
    const cargar = async () => {
      try {
        const data = await getUsers();
        setUsuarios(data);
      } catch (err) {
        setError('Error al cargar usuarios.');
      } finally {
        setCargando(false);
      }
    };
    cargar();
  }, []);

  const actualizarUsuario = (usuarioActualizado) => {
    setUsuarios(prev => prev.map(u => u.id === usuarioActualizado.id ? usuarioActualizado : u));
  };

  const handleAsignar = async (userId) => {
    const prof = selecciones[userId] ?? '';
    try {
      const updated = await updateUser(userId, 'assign_profession', { profession: prof });
      actualizarUsuario(updated);
    } catch (err) {
      setError(err.message);
    }
  };

  const handleAprobar = async (userId) => {
    try {
      const updated = await updateUser(userId, 'approve_request');
      actualizarUsuario(updated);
    } catch (err) {
      setError(err.message);
    }
  };

  const handleRechazar = async (userId) => {
    try {
      const updated = await updateUser(userId, 'reject_request');
      actualizarUsuario(updated);
    } catch (err) {
      setError(err.message);
    }
  };

  const handleToggleStaff = async (userId) => {
    try {
      const updated = await updateUser(userId, 'toggle_staff');
      actualizarUsuario(updated);
    } catch (err) {
      setError(err.message);
    }
  };

  const handleActivarProfesional = async (userId) => {
    try {
      const updated = await updateUser(userId, 'activate_professional');
      actualizarUsuario(updated);
    } catch (err) {
      setError(err.message);
    }
  };

  // Refresca los contadores de trial mientras el modal está abierto.
  const [, forzarActualizacionTrial] = useState(0);
  useEffect(() => {
    const id = setInterval(() => forzarActualizacionTrial((t) => t + 1), 60000);
    return () => clearInterval(id);
  }, []);

  const profesionales = usuarios.filter(u => !u.is_superuser && u.date_joined);
  const profesionalesActivos = profesionales.filter(u => u.activated_by_admin);
  const profesionalesEnTrial = profesionales.filter(u => !u.activated_by_admin);

  return (
    <div className="modal-overlay" onClick={(e) => e.target === e.currentTarget && onCerrar()}>
      <div className="modal-card modal-card-wide">
        <div className="modal-header">
          <h3>Administrar usuarios</h3>
          <button className="modal-btn-cerrar" onClick={onCerrar}>✕</button>
        </div>

        <div className="modal-body">
          {error && <div className="modal-mensaje-error">{error}</div>}

          {cargando ? (
            <p className="sesiones-vacio">Cargando usuarios...</p>
          ) : (
            <div className="admin-tabla-wrapper">
              <table className="admin-tabla">
                <thead>
                  <tr>
                    <th>Usuario</th>
                    <th>Nombre</th>
                    <th>Estado</th>
                    <th>Profesión</th>
                    <th>Solicitud pendiente</th>
                    <th>Rol</th>
                    <th>Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {usuarios.map(u => {
                    const esMismoUsuario = u.id === meUser.id;
                    const seleccionado = selecciones[u.id] ?? (u.profession || '');

                    let estadoBadge;
                    if (u.profession) {
                      estadoBadge = <span className="admin-badge-confirmado">Confirmado</span>;
                    } else if (u.profession_request) {
                      estadoBadge = <span className="admin-badge-pendiente">Pendiente</span>;
                    } else {
                      estadoBadge = <span className="admin-badge-sin-asignar">Sin asignar</span>;
                    }

                    return (
                      <tr key={u.id}>
                        <td><strong>{u.username}</strong></td>
                        <td>{u.first_name} {u.last_name}</td>
                        <td>{estadoBadge}</td>
                        <td>
                          {u.profession_display
                            ? <span className="admin-badge-profesion">{u.profession_display}</span>
                            : <span style={{ color: '#ccc' }}>—</span>
                          }
                        </td>
                        <td>
                          {u.profession_request
                            ? <span className="admin-badge-request">{u.profession_request_display || u.profession_request}</span>
                            : <span style={{ color: '#ccc' }}>—</span>
                          }
                        </td>
                        <td>
                          {u.is_superuser && <span className="admin-badge-superuser">Superuser</span>}
                          {u.is_staff && !u.is_superuser && <span className="admin-badge-staff">Staff</span>}
                          {!u.is_staff && !u.is_superuser && <span style={{ color: '#aaa', fontSize: 12 }}>Usuario</span>}
                        </td>
                        <td>
                          <div className="admin-acciones">
                            {/* Asignar profesion */}
                            {!esMismoUsuario && !u.is_superuser && (
                              <>
                                <select
                                  className="admin-select"
                                  value={seleccionado}
                                  onChange={e => setSelecciones(prev => ({ ...prev, [u.id]: e.target.value }))}
                                >
                                  {PROFESSION_CHOICES.map(p => (
                                    <option key={p.value} value={p.value}>{p.label}</option>
                                  ))}
                                </select>
                                <button className="admin-btn admin-btn-asignar" onClick={() => handleAsignar(u.id)}>
                                  Asignar
                                </button>
                              </>
                            )}

                            {/* Aprobar / Rechazar solicitud */}
                            {u.profession_request && !esMismoUsuario && (
                              <>
                                <button className="admin-btn admin-btn-aprobar" onClick={() => handleAprobar(u.id)}>
                                  Aprobar
                                </button>
                                <button className="admin-btn admin-btn-rechazar" onClick={() => handleRechazar(u.id)}>
                                  Rechazar
                                </button>
                              </>
                            )}

                            {/* Toggle staff (no puede modificar superusers ni a si mismo) */}
                            {!esMismoUsuario && !u.is_superuser && (
                              <button
                                className={`admin-btn ${u.is_staff ? 'admin-btn-staff-off' : 'admin-btn-staff-on'}`}
                                onClick={() => handleToggleStaff(u.id)}
                              >
                                {u.is_staff ? 'Quitar staff' : 'Dar staff'}
                              </button>
                            )}

                            {esMismoUsuario && (
                              <span style={{ fontSize: 12, color: '#aaa' }}>Tu cuenta</span>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {!cargando && (
            <div className="admin-trial-seccion">
              <h4>Período de prueba</h4>

              <div className="admin-trial-columna">
                <h5>Profesionales activos ({profesionalesActivos.length})</h5>
                {profesionalesActivos.length === 0 ? (
                  <p className="sesiones-vacio">Ninguno todavía.</p>
                ) : (
                  <ul className="admin-trial-lista">
                    {profesionalesActivos.map(u => (
                      <li key={u.id}>
                        <strong>{u.username}</strong> — {u.first_name} {u.last_name}
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              <div className="admin-trial-columna">
                <h5>Profesionales en trial ({profesionalesEnTrial.length})</h5>
                {profesionalesEnTrial.length === 0 ? (
                  <p className="sesiones-vacio">Ninguno en trial.</p>
                ) : (
                  <ul className="admin-trial-lista">
                    {profesionalesEnTrial.map(u => {
                      const restanteMs = calcularRestanteTrial(u.date_joined);
                      const vencido = restanteMs <= 0;
                      return (
                        <li key={u.id}>
                          <strong>{u.username}</strong> — {u.first_name} {u.last_name}
                          {' — '}
                          {vencido
                            ? <span className="admin-trial-vencido">🔴 Vencido</span>
                            : <span className="admin-trial-contador">{formatearRestanteTrial(restanteMs)} restantes</span>
                          }
                          <button className="admin-btn admin-btn-asignar" onClick={() => handleActivarProfesional(u.id)}>
                            Activar
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default ModalAdminUsuarios;
